import { LicenseModalComponent } from './licenses-modal/licenses-modal.component';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  AlertifyService,
  ConfirmModalComponent,
  GenericTableConfig,
  Header,
  Row,
} from 'ccc-commons/core';
import { License } from '@ccc-core/models/license.model';
import { LicensesService } from '@ccc-core/services/licenses.service';
import { ActivatedRoute, Router } from '@angular/router';
import { OrganizationStore } from '../../../organization/stores/organization.store';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { GeneralConfigurationService } from '@ccc-core/services/general-configuration.service';

@Component({
  selector: 'ccc-cca-licenses',
  templateUrl: './licenses.component.html',
  styleUrls: ['./licenses.component.scss']
})
export class LicensesComponent implements OnInit, OnDestroy {


  @ViewChild('modalCancelChanges')
  modalCancelChanges: ConfirmModalComponent;

  organizationId: string;
  organizationName: string;


  licensesTableConfig: GenericTableConfig;
  licensesHeaders: Header[] = [];
  licensesRow: Row[] = [];

  licenses: License[] = [];
  licensesType = [];

  programProdCodes = [];

  editingElement: License;
  licenceBeforeChanges = {
    docType: '',
    licenseKey: '',
    orgName: '',
    orderRef: '',
    startDate: '',
    endDate: '',
    externalProductId: '',
    purchasedSeats: 0,
    notes: '',
    id: ''
  };
  isEditing = false;
  isCreating = false;
  isSending = false;
  isLoading = false;
  errors = [];
  startDate;
  endDate;
  previousPath = '';

  // table and pagination config
  totalItems = 0;
  currentPage = 1;
  itemsPerPage = [10, 15, 20, 25]; // TODO: ask for this numbers
  limitPerPage = this.itemsPerPage[0];
  limitPagination = 3;
  params: any = {};
  sortUpType: boolean;
  showing: number;

  @ViewChild('licenseModal')
  licenseModal: LicenseModalComponent;
  sortUpOrderRef: boolean;
  sortUpStartDate: boolean;
  sortUpEndDate: boolean;

  private destroySubject = new Subject<void>();

  constructor(
    private router: Router,
    private licenseService: LicensesService,
    private route: ActivatedRoute,
    private alertifyService: AlertifyService,
    private organizationStore: OrganizationStore,
    private configurationService: GeneralConfigurationService,
  ) {}

  ngOnInit() {
    const extras = this.router.getCurrentNavigation()?.extras?.state;
    this.previousPath = extras ? extras.previousPath : '/home';

    this.generateHeaders();
    this.configureTable();
    this.getLicenseConfiguration();


    this.organizationStore.organization
      .pipe(
        takeUntil(this.destroySubject)
      )
      .subscribe((org) => {
        this.organizationId = org.id;
        this.organizationName = org.name;
        this.licenses = [];
        this.isLoading = true;
        this.getLicensesWithFilter();
        this.getProgramLicenseCodes();
      });
  }

  ngOnDestroy() {
    this.destroySubject.next();
    this.destroySubject.complete();
  }


  generateHeaders() {
    const type = new Header('TYPE');
    type.sortField = 'docType';
    this.licensesHeaders.push(type);

    const orderNumber = new Header('ORDER NUMBER');
    orderNumber.sortField = 'orderRef';
    this.licensesHeaders.push(orderNumber);

    const orgName = new Header('ORGANIZATION NAME');
    this.licensesHeaders.push(orgName);

    const orderDate = new Header('ORDER DATE');
    orderDate.sortField = 'startDate';
    this.licensesHeaders.push(orderDate);

    const expiryDate = new Header('EXPIRY DATE');
    expiryDate.sortField = 'endDate';
    this.licensesHeaders.push(expiryDate);

    const programLicenseCode = new Header('Program License Code');
    this.licensesHeaders.push(programLicenseCode);

    const quantity = new Header('QUANTITY');
    this.licensesHeaders.push(quantity);

    const licenseKey = new Header('LICENSE KEY');
    this.licensesHeaders.push(licenseKey);

    const notes = new Header('NOTES');
    this.licensesHeaders.push(notes);

    const actions = new Header('ACTIONS');
    this.licensesHeaders.push(actions);
  }

  /***
   * sorts the table
   * @param field to order by
   */
  public onSort(field: string): void {
    this.params.sort = field;
    this.getLicensesWithFilter();
  }

  private configureTable() {
    this.licensesTableConfig = {
      headers: this.licensesHeaders,
      rows: this.licensesRow,
      typeOfItems: 'Licenses',
      resizeWidth: false
    };
  }

  getLicensesWithFilter() {
    this.params.skip = (this.currentPage - 1) * this.limitPerPage;
    this.params.limit = this.limitPerPage;

    this.licenseService.getOrganizationLicenses(this.organizationId, this.params).subscribe(
      response => {
        this.licenses = response.body;
        this.isLoading = false;
        this.totalItems = response.pagination.total;
        this.showing = response.pagination.limit;
        this.setTableConfig();
      },
      error => {
        this.alertifyService.error('Error loading licenses');
      }
    );
  }



  getLicenseConfiguration() {
    if (this.configurationService.config) {
      for (const obj of this.configurationService.config) {
        if (obj.type === 'transaction_doc_type') {
          this.licensesType = obj.values;
        }
      }
    }
  }

  /**
   * getProgramLicenseCodes
   * get the code of all the licenses available for the selected organization
   * and sort them alphabetically
   */
  private getProgramLicenseCodes() {
    this.licenseService.getProgramLicenseCodes()
      .pipe(takeUntil(this.destroySubject))
      .subscribe(resp => {
      this.programProdCodes = resp.body.sort();
    }, error => {
      this.alertifyService.error('Something went wrong');
    });
  }

  onAddNewLicense() {
    this.licenseModal.open();
  }


  onFunctionEmitted(functionEmitted) {
    const functionAsArray = functionEmitted.split(',');

    const functionToCall = functionAsArray[0];

    if (functionToCall === 'limitPerPageChanged') {
      const newLimitPerPage = functionAsArray[1];
      this.currentPage = 1;
      this.onItemPerPageSelected(newLimitPerPage);
    }
    this.setTableConfig();
  }

  editLicense(license) {
    this.licenseModal.openToEdit({ ...license });
  }

  setTableConfig() {
    this.licensesTableConfig = new GenericTableConfig(
      this.licensesHeaders,
      this.licensesRow,
      this.limitPerPage,
      this.totalItems,
      this.currentPage,
      this.limitPagination,
      this.itemsPerPage,
      'Licenses',
      this.showing
    );
    this.licensesTableConfig.resizeWidth = false;
  }

  onPageChange(event) {
    this.currentPage = event;
    this.getLicensesWithFilter();
  }

  onItemPerPageSelected(item) {
    this.limitPerPage = item;
    this.getLicensesWithFilter();
  }

  onUpdateLicenses(message) {
    this.alertifyService.success(message);
    this.getLicensesWithFilter();
    this.licenseModal.onClose();

  }


}
